import type { ReactNode } from 'react'
import type { EditorMode } from '@/msgdeskRuntime/contracts/editorActions'
import { MainContent } from '@/deskUiSkeletons/Page/MainContent'
import { legacyContractAttrs, legacyHookAttrs, type LegacyContractName } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { selectMainHeadingText, selectMainState } from '@/msgdeskRuntime/state'
import { styleFromString } from '@/msgdeskRuntime/legacyBridge/legacyActions'
import { useSubsStore } from '@/state/MsgTreeProvider'
import { AnswersPanel, MenuSubsPanels, MessagesPanel, SubsToolbar } from '@/deskUiSkeletons/Page/Panels'
import { EnterPinForm, LoadingOverlay, QuickReply, ShellOverlays } from '@/deskUiSkeletons/Shared/Overlays'
import { PinDialog } from '@/deskUiSkeletons/Shared/PinDialog'
import { ContactsMenu } from './ContactsMenu'
import { ContactsEditorTemplate } from './editor/ContactsEditorTemplate'
import { ContactsFoldSubsButton } from './panels/ContactsControls'
import { ContactsMainAttachments } from './ContactsMainAttachments'

type WorkspaceKind = 'main' | 'editor' | 'attachments' | 'favs' | 'trash' | 'drafts'

const workspaceByKind: Record<WorkspaceKind, { contract: LegacyContractName; style: string }> = {
  main: { contract: 'workspace.main', style: '' },
  editor: { contract: 'workspace.editor', style: 'height:100%;' },
  attachments: { contract: 'workspace.attachments', style: '' },
  favs: { contract: 'workspace.favs', style: '' },
  trash: { contract: 'workspace.trash', style: '' },
  drafts: { contract: 'workspace.drafts', style: '' },
}

function WorkspaceSection({ kind, children }: { kind: WorkspaceKind; children?: ReactNode }) {
  const { state } = useSubsStore()
  const info = workspaceByKind[kind]
  const visible = state.chromeByContract[info.contract]?.visible !== false
  return (
    <section
      {...legacyHookAttrs(info.contract, `md-contacts-workspace md-contacts-workspace--${kind} min-w-0${visible ? '' : ' hidden'}${kind === 'main' || kind === 'editor' ? ' flex-1' : ''}`)}
      style={{ ...styleFromString(info.style), ...(visible ? {} : { display: 'none' }) }}
      {...(visible ? {} : { 'data-md-react-display': 'none' })}
    >
      {children}
    </section>
  )
}

function ContactsMainMessage() {
  const { state } = useSubsStore()
  const main = selectMainState(state)
  const title = selectMainHeadingText(state) || 'Contacts'

  return (
    <article {...legacyHookAttrs('main.root', 'md-contacts-main mb-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm')}>
      <header {...legacyHookAttrs('main.header', 'md-contacts-main__header flex items-start justify-between gap-4')}>
        <div className="min-w-0">
          <div {...legacyHookAttrs('main.avatarFrame', 'hidden')} hidden aria-hidden="true">
            <img {...legacyContractAttrs('main.avatarImage')} alt="" />
          </div>
          <span {...legacyHookAttrs('main.nameKeySign', 'hidden')} hidden aria-hidden="true">@</span>
          <span {...legacyHookAttrs('main.authorFrame', 'text-xs font-semibold uppercase tracking-[0.18em] text-violet-600')}>
            {main.author || main.nameKey || 'msgdesk contacts'}
          </span>
          <div {...legacyHookAttrs('main.emailRegion', 'hidden')} hidden aria-hidden="true" />
          <div {...legacyHookAttrs('main.subjectRegion', 'mt-1')}>
            <div {...legacyHookAttrs('main.subjectLine', '')}>
              <h1 {...legacyHookAttrs('main.subjectText', 'break-words text-2xl font-semibold text-slate-950')}>{title}</h1>
            </div>
            <div {...legacyHookAttrs('main.lineClear', 'hidden')} hidden aria-hidden="true" />
          </div>
        </div>
        <span {...legacyHookAttrs('main.datetime', 'flex-none text-xs text-slate-500')}>{main.datetime || ''}</span>
      </header>
      <div {...legacyHookAttrs('main.quad', 'mt-4')}>
        <div {...legacyHookAttrs('main.body', 'relative')}>
          <ContactsFoldSubsButton kind="msg" label="Fold description" />
          <div {...legacyHookAttrs('main.iframe', 'mt-3 max-w-4xl text-sm leading-6 text-slate-700 [&_img]:max-w-full')}>
            <MainContent frame={{ kind: 'solid' }} />
          </div>
        </div>
        <div {...legacyHookAttrs('main.editMount', 'hidden')} hidden aria-hidden="true" />
      </div>
    </article>
  )
}

export function ContactsPage({
  isEditorOpen = false,
  editorMode,
  settingsOpen,
  onToggleSettings,
  onCloseSettings,
}: {
  isEditorOpen?: boolean
  editorMode?: EditorMode
  settingsOpen: boolean
  onToggleSettings: () => void
  onCloseSettings: () => void
}) {
  const { state } = useSubsStore()
  const editorActive = isEditorOpen || state.renderProjection.mode === 'editor'

  return (
    <>
      <ShellOverlays />
      <div {...legacyHookAttrs('page.root', 'md-contacts-page min-h-screen w-full bg-slate-50 text-slate-900')}>
        <div {...legacyHookAttrs('page.table', 'md-contacts-page-table min-h-screen w-full')}>
          <div {...legacyHookAttrs('page.header', 'h-16')}>
            <div {...legacyHookAttrs('page.headerCell', 'h-16')} />
          </div>
          <div className="mx-auto flex w-full max-w-[1600px]">
            <div {...legacyHookAttrs('page.accentCell', 'hidden')} hidden aria-hidden="true" />
            <main {...legacyHookAttrs('page.workspaceCell', 'min-w-0 flex-1 px-4 pb-10 sm:px-6 lg:px-8')}>
              <WorkspaceSection kind="main">
                <ContactsMainMessage />
                <SubsToolbar />
                <AnswersPanel />
                <MessagesPanel />
              </WorkspaceSection>
              <WorkspaceSection kind="editor">
                <ContactsEditorTemplate active={editorActive} editorMode={isEditorOpen ? editorMode : ''} settingsOpen={settingsOpen} onCloseSettings={onCloseSettings} />
              </WorkspaceSection>
              <WorkspaceSection kind="attachments"><ContactsMainAttachments /></WorkspaceSection>
              <WorkspaceSection kind="favs"><MenuSubsPanels mode="favs" /></WorkspaceSection>
              <WorkspaceSection kind="trash"><MenuSubsPanels mode="trash" /></WorkspaceSection>
              <WorkspaceSection kind="drafts"><MenuSubsPanels mode="drafts" /></WorkspaceSection>
            </main>
            <div {...legacyHookAttrs('page.rightRail', 'hidden')} hidden aria-hidden="true" />
          </div>
          <div {...legacyHookAttrs('page.footer', 'h-8')} />
        </div>
      </div>
      <ContactsMenu
        isEditorOpen={editorActive}
        editorMode={editorMode}
        settingsOpen={settingsOpen}
        onToggleSettings={onToggleSettings}
      />
      <QuickReply />
      <EnterPinForm />
      <LoadingOverlay />
      <PinDialog />
    </>
  )
}
